import { useState, useEffect } from 'react'
import { Button } from '@/components/ui/button'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { toast } from 'sonner'
import { useAuth } from '@/lib/contexts/AuthContext'
import { timeZones } from '@/lib/constants'
import {
  getHashFragmentTrackingEnabled,
  updateHashFragmentTrackingEnabled,
} from '@/lib/api/api'
import {
  SettingsWell,
  SettingsCard,
  SettingsRow,
  SettingsField,
  SettingsToggle,
  SettingsFooter,
  settingsSaveClass,
  settingsSelectTriggerClass,
} from '@/components/shared/settings/SettingsKit'

interface GeneralTabProps {
  domain: string
  propertyId?: string
}

export default function GeneralTab({ propertyId }: GeneralTabProps) {
  const { token } = useAuth()

  const [selectedTimezone, setSelectedTimezone] = useState<string>('')
  const [hashFragmentTrackingEnabled, setHashFragmentTrackingEnabled] = useState(false)
  const [isLoadingHashTracking, setIsLoadingHashTracking] = useState(false)
  const [timezoneDirty, setTimezoneDirty] = useState(false)

  useEffect(() => {
    const storageKey = `timezone_${propertyId}`
    let currentTimezone = localStorage.getItem(storageKey)

    if (!currentTimezone) {
      currentTimezone = localStorage.getItem('timezone')
    }

    if (!currentTimezone) {
      const match = timeZones.find((tz) =>
        tz.utc.includes(Intl.DateTimeFormat().resolvedOptions().timeZone),
      )
      if (match) {
        setSelectedTimezone(match.utc[0])
      }
    } else {
      setSelectedTimezone(currentTimezone)
    }

    setTimezoneDirty(false)
    loadHashFragmentTracking()
  }, [propertyId])

  const loadHashFragmentTracking = async () => {
    if (!token || !propertyId) return

    try {
      setIsLoadingHashTracking(true)
      const response = await getHashFragmentTrackingEnabled(propertyId, token)
      setHashFragmentTrackingEnabled(response?.trackFragments || false)
    } catch (error) {
      console.error('Error loading hash fragment tracking setting:', error)
    } finally {
      setIsLoadingHashTracking(false)
    }
  }

  const toggleHashFragmentTracking = async (newValue: boolean) => {
    const previousValue = hashFragmentTrackingEnabled
    setHashFragmentTrackingEnabled(newValue)

    if (!token || !propertyId) {
      setHashFragmentTrackingEnabled(previousValue)
      return
    }

    try {
      const response = await updateHashFragmentTrackingEnabled(
        propertyId,
        newValue,
        token,
      )
      setHashFragmentTrackingEnabled(
        typeof response?.hashFragmentTrackingEnabled === 'boolean'
          ? response.hashFragmentTrackingEnabled
          : newValue,
      )
      toast.success(
        newValue
          ? 'URL fragment tracking on'
          : 'URL fragment tracking off',
      )
    } catch (error) {
      console.error('Error updating hash fragment tracking setting:', error)
      setHashFragmentTrackingEnabled(previousValue)
      toast.error("Couldn't update URL fragment tracking")
    }
  }

  const updateTimezone = () => {
    if (selectedTimezone && propertyId) {
      const storageKey = `timezone_${propertyId}`
      localStorage.setItem(storageKey, selectedTimezone)
      setTimezoneDirty(false)
      toast.success('Timezone saved. Refresh to apply it to charts.')
    }
  }

  const currentTimezoneLabel = timeZones.find((tz) =>
    tz.utc.includes(selectedTimezone),
  )?.text

  return (
    <SettingsWell>
      <SettingsCard>
        <SettingsRow
          title="Timezone"
          description="Day boundaries for this site's charts. Overrides the workspace default in this browser."
        >
          <SettingsField label="Timezone" htmlFor="site-timezone">
            <Select
              value={selectedTimezone}
              onValueChange={(v) => {
                setSelectedTimezone(v)
                setTimezoneDirty(true)
              }}
            >
              <SelectTrigger id="site-timezone" className={settingsSelectTriggerClass}>
                <SelectValue placeholder="Choose your timezone">
                  {currentTimezoneLabel || 'Choose your timezone'}
                </SelectValue>
              </SelectTrigger>
              <SelectContent className="max-h-[300px]">
                {timeZones.map((tz) => (
                  <SelectItem key={tz.value + tz.offset} value={tz.utc[0] || tz.value}>
                    {tz.text}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </SettingsField>
        </SettingsRow>
        <SettingsRow
          title="URL fragments (#)"
          description="Count #pricing and #faq as separate pages on this site. Overrides the workspace default."
        >
          <SettingsToggle
            title="Track URL fragments"
            description="Useful for single-page apps and in-page sections"
            checked={hashFragmentTrackingEnabled}
            disabled={isLoadingHashTracking}
            onCheckedChange={toggleHashFragmentTracking}
          />
        </SettingsRow>
        <SettingsFooter>
          <Button onClick={updateTimezone} disabled={!timezoneDirty} className={settingsSaveClass}>
            Save changes
          </Button>
        </SettingsFooter>
      </SettingsCard>
    </SettingsWell>
  )
}
